import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AVATARS, NAME_MAX, normalizeRoomCode } from '@shared/rules';
import type { PackSummary } from '@shared/types';
import { Footer, TopBar, useDocumentTitle } from '../components/Chrome';
import { api } from '../lib/api';
import { call } from '../lib/socket';
import { getAvatar, getName, saveAvatar, saveName, saveSeat } from '../lib/storage';
import { Demo } from './marketing/Demo';

const FEATURES: { icon: string; title: string; text: string }[] = [
  { icon: '\u{1F524}', title: 'Translations', text: 'Hindi and Urdu lines show a short English translation, so the cousins born abroad keep up.' },
  { icon: 'ℹ️', title: 'The (i) button', text: 'Tap it to learn which film, song or custom a card is about. No more explaining Mogambo.' },
  { icon: 'अ', title: 'Devanagari', text: 'Show lines in Hindi script too, for the elders who read it faster than English.' },
  { icon: '\u{1F50A}', title: 'Read aloud', text: 'The phone reads the prompt out, for small screens and judges who butcher the Hindi.' },
  { icon: '\u{1F4FA}', title: 'TV mode', text: 'Put the prompt, the card flips and the winner on the big screen, with a QR code to join.' },
  {
    icon: '\u{1F389}',
    title: 'Extra masti',
    text: 'Kids vs Parents teams, Guess who played it, Sabse Bekaar rounds for the worst card, and prompts you write yourselves.',
  },
  { icon: '\u{1F0CF}', title: 'Custom packs', text: 'Write your family inside jokes into a pack and play them in any game.' },
  { icon: '\u{1F4F1}', title: 'One-phone mode', text: 'Not everyone has a phone? Pass one around the room and play together.' },
  { icon: '\u{1F3AD}', title: 'Mini-games', text: 'Dumb Charades, Finish the Dialogue and Mat Bolna, all on a single phone.' },
];

const FAQ: { q: string; a: React.ReactNode }[] = [
  {
    q: 'Is it free?',
    a: (
      <>
        Yes. Playing on your phones is free, with no ads and no sign-up. If you'd like real cards on the table, we also make <Link to="/shop">printed decks</Link>.
      </>
    ),
  },
  { q: 'Do we need to download an app?', a: 'No. It runs in the browser. One person starts a room and everyone else joins with the 4-letter code or the invite link.' },
  {
    q: 'How many people can play?',
    a: (
      <>
        3 to 16 players, and it's best with 4 to 10. Fewer phones than people? Try <Link to="/games/one-phone">one-phone mode</Link>.
      </>
    ),
  },
  {
    q: 'Is it OK to play with my parents?',
    a: 'The Classic and Filmy packs are made for the whole family. The 18+ Besharam pack stays off unless the host turns it on, and it asks for a confirmation first.',
  },
  {
    q: 'Can we play over a video call?',
    a: (
      <>
        Yes. Switch the room to "everyone votes" so nobody has to judge, and share TV mode on the call. See our tips for <Link to="/family-zoom-games">family Zoom games</Link>.
      </>
    ),
  },
  {
    q: 'Can we add our own jokes?',
    a: (
      <>
        Everyone gets blank cards to write in their own answers, and you can <Link to="/packs/new">make a whole pack</Link> of family inside jokes in about ten
        minutes.
      </>
    ),
  },
  {
    q: 'Can we use it at a wedding or a big event?',
    a: (
      <>
        Yes, and we can help. See the <Link to="/events">event host kit</Link> for sangeets, student associations and office Diwali parties.
      </>
    ),
  },
];

const MORE: { to: string; cta: string; title: string; text: string }[] = [
  { to: '/games', cta: 'home_more_games', title: 'Party games', text: 'Dumb Charades, Mat Bolna and more on one phone.' },
  { to: '/packs/new', cta: 'home_more_make_pack', title: 'Make your own pack', text: 'Inside jokes only your family gets.' },
  { to: '/shop', cta: 'home_more_shop', title: 'Printed decks', text: 'Real cards for the table, or a deck of your own pack.' },
  { to: '/events', cta: 'home_more_events', title: 'Weddings and events', text: 'Sangeets, student nights and office Diwali.' },
  { to: '/how-to-play', cta: 'home_more_how', title: 'How to play', text: 'One judge, one prompt, the funniest card wins.' },
  { to: '/suggest', cta: 'home_more_suggest', title: 'Suggest a card', text: 'Got a line that belongs in the box?' },
];

function PackPreviews() {
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  useEffect(() => {
    api
      .packs()
      .then(setPacks)
      .catch(() => setPacks([]));
  }, []);
  if (packs && !packs.length) return null;
  return (
    <section className="mk-section">
      <div className="row between">
        <h2 style={{ margin: 0 }}>The packs</h2>
        <Link to="/packs" className="small" data-cta="home_packs_all">
          See all packs
        </Link>
      </div>
      <div className="mk-packs">
        {(packs ?? []).slice(0, 6).map((p) => (
          <Link key={p.id} to={`/packs/${p.code}`} className="mk-pack" data-cta="home_pack">
            <strong>
              {p.name} {p.nsfw && <span className="chip red">18+</span>}
            </strong>
            {p.description && <span className="mk-pack-desc">{p.description}</span>}
            <span className="muted small">
              {[p.promptCount && `${p.promptCount} prompts`, p.answerCount && `${p.answerCount} answers`].filter(Boolean).join(' · ')}
            </span>
          </Link>
        ))}
        {!packs && [0, 1, 2].map((i) => <div key={i} className="mk-pack mk-skeleton" aria-hidden="true" />)}
      </div>
    </section>
  );
}

export function Home() {
  useDocumentTitle('');
  const navigate = useNavigate();
  const [name, setName] = useState(getName());
  const [avatar, setAvatar] = useState(getAvatar() || AVATARS[Math.floor(Math.random() * AVATARS.length)]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'create' | 'join' | null>(null);
  const [error, setError] = useState('');
  const [pickAvatar, setPickAvatar] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);

  function remember() {
    saveName(name.trim());
    saveAvatar(avatar);
  }

  async function create() {
    setError('');
    if (!name.trim()) return setError('What should we call you?');
    setBusy('create');
    remember();
    const res = await call('room:create', { name, avatar });
    setBusy(null);
    if (!res.ok) return setError(res.error);
    saveSeat(res.code, { token: res.token, playerId: res.playerId });
    navigate(`/game/${res.code}`);
  }

  async function join(e?: React.FormEvent) {
    e?.preventDefault();
    setError('');
    if (!name.trim()) return setError('What should we call you?');
    const room = normalizeRoomCode(code);
    if (room.length !== 4) return setError('Room codes are 4 letters.');
    setBusy('join');
    remember();
    const res = await call('room:join', { code: room, name, avatar });
    setBusy(null);
    if (!res.ok) return setError(res.error);
    saveSeat(res.code, { token: res.token, playerId: res.playerId });
    navigate(`/game/${res.code}`);
  }

  function backToStart() {
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    nameInput.current?.focus({ preventScroll: true });
  }

  return (
    <>
      <TopBar />
      <main className="page">
        <section className="hero home-hero">
          <img className="logo" src="/logo-mark.png" alt="CompleteMasti logo" />
          <h1 className="sr-only">CompleteMasti</h1>
          <p className="tagline">A brown-family card game for all generations</p>
          <p className="credit">Fill in the blank. Roast the family. Nobody tells Nani.</p>
        </section>

        <section className="panel stack" style={{ marginTop: 8 }}>
          <label className="field">
            Your name
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <button
                type="button"
                className="avatar lg"
                style={{ border: 'none', cursor: 'pointer' }}
                aria-label="Choose your emoji"
                onClick={() => setPickAvatar((v) => !v)}
              >
                {avatar}
              </button>
              <input
                ref={nameInput}
                className="input"
                value={name}
                maxLength={NAME_MAX}
                autoComplete="nickname"
                placeholder="Bunty, Pinky, Dadi..."
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </label>
          {pickAvatar && (
            <div className="avatar-grid" role="group" aria-label="Pick an emoji">
              {AVATARS.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={a === avatar}
                  onClick={() => {
                    setAvatar(a);
                    setPickAvatar(false);
                  }}
                >
                  {a}
                </button>
              ))}
            </div>
          )}

          <button className="btn btn-primary btn-lg btn-block" onClick={create} disabled={busy !== null} data-cta="home_create">
            {busy === 'create' ? 'Setting up the room...' : 'Start a new game'}
          </button>

          <div className="row" style={{ gap: 12 }}>
            <hr className="divider grow" />
            <span className="muted small">or join friends</span>
            <hr className="divider grow" />
          </div>

          <form className="row" style={{ flexWrap: 'nowrap' }} onSubmit={join}>
            <input
              className="input code"
              value={code}
              onChange={(e) => setCode(normalizeRoomCode(e.target.value))}
              placeholder="CODE"
              aria-label="Room code"
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={4}
            />
            <button className="btn btn-blue btn-lg" disabled={busy !== null} data-cta="home_join">
              {busy === 'join' ? 'Joining...' : 'Join'}
            </button>
          </form>
          {error && <p className="error" role="alert">{error}</p>}
        </section>
        <p className="muted small center home-free">Free on your phones. No app, no sign-up.</p>

        <section className="mk-section">
          <h2>A round in ten seconds</h2>
          <Demo />
        </section>

        <section className="mk-section">
          <h2>How it works</h2>
          <ol className="mk-how">
            <li>
              <span className="mk-how-n" aria-hidden="true">1</span>
              <strong>Start a room</strong>
              <span>Share the 4-letter code or the WhatsApp invite. Everyone joins from their own phone.</span>
            </li>
            <li>
              <span className="mk-how-n" aria-hidden="true">2</span>
              <strong>Play your funniest card</strong>
              <span>A prompt with a blank comes up. Everyone picks an answer from their hand.</span>
            </li>
            <li>
              <span className="mk-how-n" aria-hidden="true">3</span>
              <strong>Crown the winner</strong>
              <span>The judge, or the whole room, picks the best one. First to the target score wins.</span>
            </li>
          </ol>
        </section>

        <section className="mk-section">
          <h2>Made for mixed crowds</h2>
          <p className="muted">Nani, the cousins from Jersey and the eight-year-old can all play the same game.</p>
          <div className="mk-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="mk-tile">
                <span className="mk-icon" aria-hidden="true">
                  {f.icon}
                </span>
                <strong>{f.title}</strong>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </section>

        <PackPreviews />

        <section className="mk-section">
          <h2>More ways to play</h2>
          <div className="links-grid">
            {MORE.map((m) => (
              <Link key={m.to} className="link-tile" to={m.to} data-cta={m.cta}>
                <strong>{m.title}</strong>
                <span>{m.text}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mk-section">
          <h2>Questions</h2>
          <div className="mk-faq">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mk-closing">
          <h2>Ready when the family is</h2>
          <p>Start a room, send the code to the group, and play your first round in about a minute.</p>
          <button type="button" className="btn btn-primary btn-lg" onClick={backToStart} data-cta="home_closing_start">
            Start a game
          </button>
        </section>
      </main>
      <Footer />
    </>
  );
}
